import * as React from 'react';
import { resolve } from "inversify-react";
import { connect } from "react-redux";
import { Dispatch } from 'redux';
import { observer } from 'mobx-react';

import { ContractGeneralView } from "./contract-general.view";
import { CardController } from "src/app/shared/card.controller";
import { ObjectUtils } from 'src/core/utils/object';
import { ToastNotifications } from 'src/core/ui/toast';
import { AllotmentDataService } from "src/app/products/allotment/form/allotment-form.data";
import { ContractRangeDateListDataService } from 'src/app/products/contract/form/contract-range-date.data';
import { FormatService } from 'src/core/services/format.service';
import { actionCreators } from "src/store/products/product-form-nav-actions";
import { BookemZulu as Model } from 'src/core/services/data/JayDataContext';
import { actionAvailabilitiesCreators } from "src/store/allotments/availabilities-actions";

interface PropsFromState {
	allotment: AllotmentItem;
	dates: ContractRangeDateItem[];
}

interface PropsFromDispatch {
	navigateToAllotment: (allotment: AllotmentItem, title: string, text: string, key: string) => any;
	allotmentLoaded: (contractItem: ContractItem, allotment: AllotmentItem) => any;
	datesLoaded: (contractItem: ContractItem, dates: ContractRangeDateItem[]) => any;
	loadAvailabilities: (supplierId: number, allotment: AllotmentItem) => any;
}

interface Props extends ComponentViewProps<ContractItem> {
	mode: ControlMode;
	onModeChanged: (mode: ControlMode) => void;
	supplierType: number;
	supplierChange: (newVal: SupplierSummary) => void;
	contextService: any;
	defaultLanguaje: string;
	onValidateGeneralInfo: (contract: ContractItem) => void;
	product: ProductItem | TourItineraryItem;
}
@observer
class ContractGeneralController extends React.Component<Props & PropsFromDispatch & PropsFromState, {}> {
	protected dataService: ContractRangeDateListDataService;
	protected allotmentDataService: AllotmentDataService;

	@resolve
	protected toast: ToastNotifications;

	@resolve
	protected formatService: FormatService;
	
	public componentDidMount() {
		this.dataService = ContractRangeDateListDataService.make(this.props.contextService, this.formatService);
		this.allotmentDataService = AllotmentDataService.make(this.props.contextService);
		this.loadData();
	}
	
	componentWillReceiveProps(nextProps: Readonly<Props & PropsFromDispatch & PropsFromState>, nextContext: any) {
		
	}
	protected async loadData(): Promise<void> {
		const { model, setBusy, dates, datesLoaded } = this.props;

		if (dates == null && model.id) {
			this.dataService.addFilter('contractId', 'eq', this.props.model.id);
			this.dataService.addSorting('bookingDateFrom', 'asc', true);
			this.dataService.pageSize = 20;
			try {
				setBusy(true);
				const items = await this.dataService.load(query => query);
				datesLoaded(model, items.slice());
			} catch (error) {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			} finally {
				setBusy(false);
			}
		} else {
			this.dataService.items = dates;
		}
	}

	onItemDoubleClicked = async (item: AllotmentItem): Promise<void> => {
		const { model, navigateToAllotment, allotmentLoaded } = this.props;
		const errors = await model.validate(false);
		if (errors.length != 0) {
			this.toast.showToast('Validation error!', errors.map(o => o.text), 'error');
			return;
		}
		if (item == null) {
			if (this.props.product.entity.getType() == Model.Products.Logic.ViewModels.ProductItem) {
				item = await this.allotmentDataService.add(this.props.product as ProductItem, this.props.model.supplierId, this.props.defaultLanguaje);
				this.props.loadAvailabilities(this.props.model.supplierId, item)
				allotmentLoaded(model, item);
			}
		}
		navigateToAllotment(item, "Allotment", item.name, 'allotment');
	};
	loadAllotmentsBysupplier = async (supplier: SupplierSummary): Promise<AllotmentItem> => {
		let allotmentInStore = this.props.allotment;
		if (allotmentInStore == null || !(allotmentInStore.supplierId == supplier.id && allotmentInStore.productId == this.props.model.productId)) {
			this.allotmentDataService.removeFilter("productId");
			this.allotmentDataService.removeFilter("supplierId");
			this.allotmentDataService.removeFilter("byDefault");
			this.allotmentDataService.removeFilter("isDeleted");
			this.allotmentDataService.removeFilter("id");
			this.allotmentDataService.addFilter('productId', 'eq', this.props.model.productId > 0 && this.props.model.tourItineraryId == null ? this.props.model.productId : this.props.model.tourItineraryId);
			this.allotmentDataService.addFilter('supplierId', 'eq', supplier.id);
			this.allotmentDataService.addFilter('byDefault', 'eq', 1);
			this.allotmentDataService.addSorting('id', 'asc', true);
			this.allotmentDataService.addFilter('isDeleted', 'eq', false);
			this.allotmentDataService.pageSize = 1;
			let allotment = null;
			const allotments = await this.allotmentDataService.load(query => query);
			if (allotments && allotments.length > 0) {
				this.props.loadAvailabilities(supplier.id, allotments[0])
				this.props.allotmentLoaded(this.props.model, allotments[0]);
				allotment = allotments[0];
			}
			this.props.supplierChange(supplier);
			return allotment;
		}
		else if (allotmentInStore == null)
			return null;
		else
			return allotmentInStore
	}
	updateAllotmentToContract = (allotment: AllotmentItem) => {
		this.props.model.allotmentId = allotment.id;
		this.props.allotmentLoaded(this.props.model, allotment);
	};
	removeAllotmentToContract = () => {
		this.props.model.allotmentId = undefined;
	};
	editAllotment = () => {
		const { allotment } = this.props;
		this.onItemDoubleClicked(allotment);
	}
	render() {
		if (this.props.model == null) {
			return null;
		}
		const model = this.props.model;
		return (
			<CardController icon='book' title="General information" mode={this.props.mode} className='card-borderless'>
				<ContractGeneralView
					mode={this.props.mode}
					model={model}
					onItemDoubleClicked={this.onItemDoubleClicked}
					setBusy={this.props.setBusy}
					supplierType={this.props.supplierType}
					supplierChange={this.props.supplierChange}
					contextService={this.props.contextService}
					allotment={this.props.allotment}
					onValidateGeneralInfo={this.props.onValidateGeneralInfo}
					loadAllotmentsBysupplier={this.loadAllotmentsBysupplier}
					updateAllotmentToContract={this.updateAllotmentToContract}
					removeAllotmentToContract={this.removeAllotmentToContract}
					editAllotment={this.editAllotment}
				/>

			</CardController>
		);
	}
}

const mapStateToProps = (
	state: IApplicationState,
	props: ComponentViewProps<ContractItem>
): PropsFromState => {
	const contract = state.otherLoadedEntities.find(e => e.entity == props.model.entity) as ContractItem;
	return {
		allotment: contract.allotment,
		dates: contract.dateBands,
	};
};

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
	navigateToAllotment: (
		entity,
		title: string,
		text: string,
		key: string
	) => dispatch(actionCreators.productFormUpNav(entity, title, text, key)),
	allotmentLoaded: (
		contract: ContractItem,
		allotment: AllotmentItem,
	) => dispatch(actionCreators.productFormRelationshipLoaded(contract, allotment, "allotment")),
	datesLoaded: (
		contract: ContractItem,
		dates: ContractRangeDateItem[],
	) => dispatch(actionCreators.productFormRelationshipLoaded(contract, dates, "dateBands")),
	loadAvailabilities: (supplierId: number, allotment: AllotmentItem) =>
		dispatch(actionAvailabilitiesCreators.loadAvailabilities(supplierId, allotment)),

});

const connected = connect(mapStateToProps, mapDispatchToProps)(ContractGeneralController);
export { connected as ContractGeneralController };



// WEBPACK FOOTER //
// ./src/app/products/contract/form/parts/contract-general.controller.tsx